// Full KS2 Student Book — Ambedkarite Buddhist Educational Series, UK Edition.
// Each chapter follows the standardised 7-step structure (the Teacher Notes section
// is held in a separate Teacher Guide and is not rendered in the Student Book).

export const BOOK_META = {
  series: "Ambedkarite Buddhist Educational Series — UK Edition",
  tagline: "Education · Equality · Enlightenment",
  level: "Junior Programme · Ages 7–11",
  subject: "Values · Citizenship · Mindfulness",
  edition: "Phase 1 · Student Book Prototype",
  age: "Ages 7–11",
};

export const CHAPTERS = [
  {
    n: 1,
    title: "What is Dignity?",
    unit: "Unit 1 · Compassion in Action",
    duration: "60 minutes",
    image: "babasaheb",
    objective: [
      { strong: "Explain", rest: " what dignity means in their own words." },
      { strong: "Describe", rest: " one event from Babasaheb's childhood and how he responded to unfairness." },
      { strong: "Identify", rest: " one example of how dignity and equality are protected in modern Britain." },
    ],
    curriculum: "Values learning — dignity, fairness and respect for everyone, with links to citizenship and the rule of law in modern Britain.",
    storyTitle: "A Boy Who Loved to Learn",
    story: [
      "When Bhimrao was a small boy in India, he loved reading more than anything. He would walk for hours just to find a new book. But because of an unfair rule called the caste system, his school did not want him to sit on the same bench as the other children.",
      "On hot days, when the other children were given water from the school tap, Bhimrao was not allowed to drink. The school caretaker would not pour water into his cup. Bhimrao went home thirsty — but he never gave up on learning.",
      "Years later, Bhimrao — now called <strong>Babasaheb Dr. B.R. Ambedkar</strong> — was chosen to write India's Constitution. The very first words he made sure it said? That every child, of every group, is <strong>equal</strong>.",
    ],
    storyDiscussion: "How do you think young Bhimrao felt when he was refused water? What gave him the strength to keep going? Have you ever stood up for someone who was being treated unfairly?",
    conceptTitle: "Dignity and Equality",
    concept: [
      "<strong>Dignity</strong> means the value every person has, simply because they are a person. You don't have to earn dignity. You don't have to be rich, fast, clever or older. You have dignity from the moment you are born.",
      "<strong>Equality</strong> means treating everyone with the same care and giving them the same chances. It does not mean everyone is the same — we are all different — it means nobody should be left out, looked down on, or refused something fair just because of who they are.",
    ],
    vocab: [
      ["dignity", "the right of every person to be treated with respect."],
      ["equality", "everyone being treated fairly and given the same chances."],
      ["constitution", "the rulebook of a country that explains everyone's rights."],
      ["compassion", "noticing when someone is hurting and wanting to help."],
    ],
    contextTitle: "Dignity in Britain Today",
    context: [
      "Britain has its own way of protecting dignity and equality. The law in this country says it is wrong to treat someone unfairly because of:",
    ],
    contextList: [
      "their religion or beliefs",
      "their race or where they come from",
      "their gender",
      "any disability they have",
      "their age, or other parts of who they are",
    ],
    contextEnd: "Schools are special places where dignity matters every single day. That is why school uniforms, pupil voice, anti-bullying rules and the role of the School Council all exist — so every child is heard and respected.",
    activityTitle: "The Dignity Wall",
    activity: [
      "<strong>Listen.</strong> The teacher reads the story of Bhimrao again, slowly.",
      "<strong>Discuss in pairs.</strong> One thing you think was unfair, and one way Bhimrao showed strength.",
      "<strong>Write.</strong> On a sticky note, finish this sentence: <em>\"Everyone in our class deserves dignity because…\"</em>",
      "<strong>Build.</strong> Together, place all sticky notes on a \"Dignity Wall\" at the front of the class.",
      "<strong>Reflect.</strong> Read the wall together. What words appear most often?",
    ],
    reflection: [
      "If you could change one rule at our school to make it kinder, what would it be?",
      "Babasaheb said reading was his superpower. What is your superpower?",
      "Tell someone at home about Babasaheb. What part of his story did you find the bravest?",
      "Can you name one person at school who always treats others with dignity? Why them?",
    ],
  },

  {
    n: 2,
    title: "The Prince Who Sought Truth",
    unit: "Unit 1 · Compassion in Action",
    duration: "60 minutes",
    image: "buddha",
    objective: [
      { strong: "Recall", rest: " who Prince Siddhartha was and what changed his life." },
      { strong: "Identify", rest: " 'the four sights' that made him ask big questions." },
      { strong: "Discuss", rest: " why asking honest questions about the world is brave." },
    ],
    curriculum: "People of faith and the big questions of life. Health & wellbeing. Fairness and respectful curiosity.",
    storyTitle: "The Four Sights",
    story: [
      "Long ago in northern India, there lived a prince called Siddhartha. His father loved him so much that he kept him inside the palace walls — surrounded by music, soft cushions and beautiful gardens. The king did not want his son to ever feel sad.",
      "But one day, the prince and his charioteer drove out beyond the palace gates. Siddhartha saw four things that changed him forever: an old man bent like a tree, a sick person trembling with fever, a body being carried to the cremation ground, and a quiet wandering teacher with a peaceful smile.",
      "\"Why do these things happen?\" he asked. \"Why does everyone suffer? Is there a way to be free?\" He left the palace that night — not to run away, but to find the truth. Years later, sitting under a fig tree, he understood. From that day, people called him <strong>the Buddha</strong> — the awakened one.",
    ],
    storyDiscussion: "What would you do if you had only ever known a happy palace and one day saw the world outside? What questions would you have?",
    conceptTitle: "Brave Questions",
    concept: [
      "The Buddha did not become wise by knowing all the answers. He became wise by being brave enough to <strong>ask honest questions</strong>. He asked: why do we feel sad? what makes us happy? how should we treat one another?",
      "These are still big questions today. We don't always have to know the answer right away. The Buddha taught that <strong>looking carefully</strong> and <strong>thinking gently</strong> is the start of wisdom.",
    ],
    vocab: [
      ["truth", "what really is, even when it is hard to see."],
      ["wisdom", "knowing the right thing to do, and doing it kindly."],
      ["enlightenment", "fully waking up to the truth of life."],
      ["palace", "a grand house where a king or queen lives."],
    ],
    contextTitle: "Big Questions, British Schools",
    context: [
      "In Britain, children are encouraged to ask questions in school. Every classroom is a place where you can put your hand up and say <em>\"why?\"</em>. Lessons about faith, life and meaning are especially full of <strong>ultimate questions</strong> — questions about life, death, kindness and what really matters. These are not silly questions. They are the most grown-up questions there are.",
    ],
    contextList: [],
    contextEnd: "When you ask a brave question respectfully, you are doing exactly what Prince Siddhartha did. You are starting your own journey of learning.",
    activityTitle: "My Big Questions Jar",
    activity: [
      "Each pupil decorates a small slip of paper.",
      "Write one question you have always wondered about. (No silly questions allowed — every wonder counts.)",
      "Fold the slip and place it in the class \"Big Questions Jar\".",
      "Each Friday, the teacher pulls out one question and the class discusses it together.",
      "Notice how some questions never get a final answer — and that's okay.",
    ],
    reflection: [
      "What is one big question you have about the world?",
      "Why do you think the Buddha looked for answers instead of going back to the palace?",
      "Who is the best 'question-asker' you know? What makes them good at it?",
      "If your school could change one thing to help children ask more questions, what would it be?",
    ],
  },

  {
    n: 3,
    title: "Compassion (Karuṇā) — When Hearts Help",
    unit: "Unit 1 · Compassion in Action",
    duration: "60 minutes",
    image: "lotus",
    objective: [
      { strong: "Define", rest: " 'compassion' (karuṇā) in their own words." },
      { strong: "Retell", rest: " the story of Kisa Gotami and the mustard seed." },
      { strong: "Plan", rest: " one act of compassion they can do this week." },
    ],
    curriculum: "Religious and cultural narratives. Relationships and mental wellbeing. Helping our community.",
    storyTitle: "The Mustard Seed",
    story: [
      "A young mother named Kisa Gotami was heartbroken. Her baby boy had died. Carrying him in her arms, she ran from house to house begging anyone to bring him back to life.",
      "At last she came to the Buddha. He looked at her with great kindness. \"Yes,\" he said softly, \"I will help. But first, please bring me a single mustard seed — from any house in this village where no one has ever lost someone they loved.\"",
      "Kisa Gotami went door to door. Every family had mustard seeds — everyone in those days did. But when she asked the second question, every family lowered their head. \"Yes,\" they said, \"we have lost a grandmother… a brother… a child of our own.\"",
      "Slowly, Kisa Gotami understood. She was not alone. Everyone, everywhere, knew this kind of pain. She returned to the Buddha not with a seed, but with a softer heart. <strong>She had learned compassion</strong>.",
    ],
    storyDiscussion: "Why did the Buddha ask Kisa Gotami to look for the mustard seed? What did she discover?",
    conceptTitle: "Karuṇā — The Helping Heart",
    concept: [
      "<strong>Karuṇā</strong> is a Pali word that means <strong>compassion</strong>. It is one of the most important ideas in the Buddha's teaching. Karuṇā has two parts:",
      "<strong>1. Noticing.</strong> Seeing when someone is sad, scared or struggling. <strong>2. Helping.</strong> Doing something — even something small — to make it a little better.",
      "Compassion is not the same as feeling sorry for someone. Compassion is rolling up your sleeves and being there.",
    ],
    vocab: [
      ["karuṇā", "the Buddhist word for compassion — noticing and helping."],
      ["empathy", "imagining how someone else feels."],
      ["volunteer", "a person who helps without being paid."],
      ["community", "a group of people who care for each other."],
    ],
    contextTitle: "Compassion in Modern Britain",
    context: [
      "Britain has many examples of karuṇā in action. Across the UK there are food banks, foster carers, the RSPCA looking after animals, the NHS treating everyone for free at the point of care, and the wonderful charities run by ordinary people in every town.",
      "When a tragedy happens — a fire, a flood, a hard winter — communities come together to help. That instinct is karuṇā. It is in all of us, just waiting to be used.",
    ],
    contextList: [],
    contextEnd: "Some famous British examples: Captain Tom Moore walking laps for the NHS, Marcus Rashford campaigning for free school meals, your local Brownies or Scouts running a litter-pick, your neighbour bringing soup when someone is unwell.",
    activityTitle: "The Compassion Tracker",
    activity: [
      "Pupils receive a simple weekly tracker (5 days, 1 box per day).",
      "Each day, do one small act of compassion — and tick the box.",
      "Examples: hold a door open, comfort a friend, share a snack, write a thank-you note, help a younger sibling.",
      "On Friday, share one act with the class. (No need to share which day.)",
      "Discuss: did doing it change how you felt?",
    ],
    reflection: [
      "Who in your life shows compassion most often? What do they do?",
      "Why do you think the Buddha taught Kisa Gotami in this gentle way, instead of just telling her?",
      "What is one act of compassion you could do at school tomorrow?",
      "Is it always easy to be compassionate? When is it hardest?",
    ],
  },

  {
    n: 4,
    title: "Mindfulness — A Quiet Mind",
    unit: "Unit 2 · The Path of Dhamma",
    duration: "55 minutes",
    image: "lotus",
    objective: [
      { strong: "Describe", rest: " what mindfulness is in simple words." },
      { strong: "Practise", rest: " one minute of mindful breathing." },
      { strong: "Explain", rest: " why a quiet mind helps with learning, sleep and friendship." },
    ],
    curriculum: "Buddhist practice and mindful living. Health, wellbeing and mental health. Connects to the wider mindfulness practices used in many British schools today.",
    storyTitle: "Under the Bodhi Tree",
    story: [
      "When Prince Siddhartha left the palace, he tried many ways to find truth. He fasted until he was thin as a reed. He stayed awake for nights. Nothing worked. One evening, exhausted, he sat down under a wide-leafed fig tree and decided simply to <strong>watch his breath</strong>.",
      "He did not push his thoughts away. He did not try to be perfect. He just noticed: breathing in, breathing out. The sound of birds. The feeling of the cool earth. His mind began to settle, like muddy water becoming clear.",
      "By morning, he understood. The tree he sat under is now called the <strong>Bodhi Tree</strong> — the tree of awakening. And the gentle practice of watching the breath is what we now call <strong>mindfulness</strong>.",
    ],
    storyDiscussion: "Why do you think watching the breath helped the Buddha when nothing else had? What does it feel like to sit very still?",
    conceptTitle: "What Mindfulness Means",
    concept: [
      "<strong>Mindfulness</strong> means paying gentle attention to what is happening right now — without rushing, without judging.",
      "When we are mindful, we notice the things we usually miss: the taste of an apple, the colour of the sky, the sound of our own breathing. We notice our feelings too — without being scared of them.",
      "The Buddha taught that mindfulness is a <strong>training</strong>, like training a puppy. Every time the mind wanders away, you gently bring it back. No telling-off. Just \"come back.\"",
    ],
    vocab: [
      ["mindfulness", "paying gentle attention to what is happening right now."],
      ["meditation", "a quiet practice of calming the mind."],
      ["bodhi tree", "the tree under which the Buddha became enlightened."],
      ["awareness", "noticing what is happening, inside and outside."],
    ],
    contextTitle: "Mindfulness in British Schools",
    context: [
      "Mindfulness has become an important part of life in the UK. Many British schools now teach short mindful-breathing practices to help children before tests, after playtime, or when feelings get big.",
      "The NHS — Britain's free health service — supports mindfulness for both adults and children. Doctors recommend it because it has been shown to help with sleep, focus, anxiety and friendships.",
    ],
    contextList: [],
    contextEnd: "Even the most modern science agrees with what the Buddha taught 2,500 years ago: a calm mind is a clearer mind.",
    activityTitle: "One-Minute Breathing",
    activity: [
      "Sit comfortably. Both feet on the floor. Hands resting in your lap.",
      "Close your eyes — or look softly at the floor.",
      "Breathe in slowly through your nose. Notice your tummy gently rising.",
      "Breathe out slowly through your nose. Notice your tummy gently falling.",
      "Do this for one minute. If your mind wanders — that's okay. Gently come back to the breath.",
      "When the bell rings, open your eyes. How do you feel?",
    ],
    reflection: [
      "When in your day could you use one minute of mindful breathing?",
      "What did your mind do during the practice — was it calm, busy, sleepy, curious?",
      "Why do you think a 'quiet mind' might help in friendships?",
      "If you could teach mindfulness to one person in your family, who would it be — and why?",
    ],
  },

  {
    n: 5,
    title: "The First School for Girls",
    unit: "Unit 3 · Reformers & Equality",
    duration: "60 minutes",
    image: "phule",
    objective: [
      { strong: "Recount", rest: " how Mahatma Phule and Savitribai opened the first school for girls in 1848." },
      { strong: "Explain", rest: " why education is sometimes called 'the key that unlocks doors'." },
      { strong: "Connect", rest: " their story to children's right to education in modern Britain." },
    ],
    curriculum: "Rights, fairness and women's education. Moral courage. History links: comparison with the Victorian era in Britain.",
    storyTitle: "Two Saris and a Brave Heart",
    story: [
      "In 1848 — when Queen Victoria sat on the British throne — most girls in India were not allowed to go to school. People said girls didn't need to read. Books were only for boys.",
      "Mahatma Jyotirao Phule and his wife Savitribai disagreed. <em>How can a country be fair</em>, they asked, <em>if half its children cannot read?</em> So they did something braver than any king's army: they opened a school for girls.",
      "Savitribai walked to the school every morning carrying two saris. Why two? Because as she walked, some people threw rotten tomatoes and even cow dung at her — angry that she dared to teach girls. When she arrived, she would change into the second clean sari and start the lesson with a smile.",
      "Day after day, year after year, she did not stop. Today, India has millions of women teachers, scientists, judges and doctors — and every single one of them stands on Savitribai's brave shoulders.",
    ],
    storyDiscussion: "What gives someone the courage to keep going even when people are unkind? Who in your life carries 'two saris' — quietly going on, no matter what?",
    conceptTitle: "Education — The Key",
    concept: [
      "Mahatma Phule called education the <strong>key</strong>. A key opens doors. With reading and writing, a person can:",
      "Earn their own living. Understand the law. Take part in their country's decisions. Stand up to unfair treatment. Help others learn.",
      "When we keep education from anyone — girls, the poor, refugees, anyone — we are locking them out of life. When we share it, we open doors for the whole community.",
    ],
    vocab: [
      ["sari", "a long piece of cloth worn as traditional Indian clothing."],
      ["literate", "able to read and write."],
      ["pioneer", "the very first person to do something brave or new."],
      ["right", "something the law says everyone must be allowed to have."],
    ],
    contextTitle: "Every Child in Britain Has the Right to Learn",
    context: [
      "In the UK, every child between 5 and 16 has the <strong>right and duty</strong> to be educated — by law. This was not always true. In Victorian times, many British children worked in factories instead of going to school. Things changed slowly, thanks to brave campaigners.",
      "Today, a girl in Manchester, a boy in Mumbai, a child in any country can read these very words because of pioneers like Savitribai Phule and the British educators who fought for school for all.",
    ],
    contextList: [],
    contextEnd: "But it's not over. There are still many parts of the world where girls cannot finish school. In our class, we can be part of the next chapter — by valuing learning, helping a friend who is struggling, and never taking school for granted.",
    activityTitle: "A Letter to Savitribai",
    activity: [
      "Each pupil writes a one-page letter to Savitribai Phule.",
      "Tell her about your school today — your favourite lesson, the things you have learned, the friends you have made.",
      "Thank her for what she did. (Without her, this letter could not be written.)",
      "Decorate the letter — drawings, leaves, anything you wish.",
      "Display the letters together as a class wall, called \"Dear Savitribai\".",
    ],
    reflection: [
      "What is one thing you've learned at school this year that you are proud of?",
      "Who would you like to thank — by name — for helping you learn?",
      "If you could open a new kind of school, what would it teach?",
      "Why do you think some people were so angry that girls were going to school? What did they fear?",
    ],
  },

  {
    n: 6,
    title: "The Power of \"Why?\"",
    unit: "Unit 3 · Reformers & Equality",
    duration: "55 minutes",
    image: "periyar",
    objective: [
      { strong: "Explain", rest: " who Periyar was and what he was famous for." },
      { strong: "Identify", rest: " the difference between asking 'why?' kindly and asking it rudely." },
      { strong: "Practise", rest: " using a respectful 'why?' to question something that seems unfair." },
    ],
    curriculum: "Discussion and democracy. Relationships and respect. Asking questions of belief and tradition with kindness.",
    storyTitle: "The Boy Who Wouldn't Stop Asking",
    story: [
      "When E.V. Ramasamy — known to the world as <strong>Periyar</strong> — was a boy in South India, the grown-ups around him followed many old rules. Some rules were lovely. Others were cruel. Periyar would tug at his uncle's sleeve and ask, \"<em>But why?</em>\"",
      "When he was told that some children could not enter the temple because of their family — he asked, \"<em>Why?</em>\". When he saw that women had to walk many steps behind men — he asked, \"<em>Why?</em>\". When people said certain jobs were only for certain groups — \"<em>Why?</em>\".",
      "He didn't shout. He didn't insult. He simply kept asking — calmly, clearly, again and again — until the unfair rules started to fall away. Periyar lived to be 94 years old. He never stopped asking why.",
    ],
    storyDiscussion: "Have you ever asked 'why?' about something the grown-ups said — and made them think? What happened?",
    conceptTitle: "Two Kinds of \"Why?\"",
    concept: [
      "There is a <strong>kind why</strong> and a <strong>rude why</strong>.",
      "A <strong>rude why</strong> is when you stamp your foot, fold your arms and demand: \"<em>Why should I do that?!</em>\" That kind doesn't really want an answer — it just wants to argue.",
      "A <strong>kind why</strong> is gentle and curious. It says: \"<em>I really want to understand. Could you help me?</em>\" That kind of why opens doors. It changes minds. It changes the world.",
    ],
    vocab: [
      ["question", "a sentence that asks for information or for thinking."],
      ["respect", "showing care for another person's feelings and ideas."],
      ["fairness", "treating everyone with equal care."],
      ["debate", "a conversation where people share different ideas."],
    ],
    contextTitle: "Why? in British Life",
    context: [
      "In Britain, asking 'why?' is at the heart of how the country works. The Houses of Parliament, where laws are made, are sometimes called the \"<em>House of Whys</em>\" — because every Wednesday the Prime Minister is asked questions for half an hour, live on TV.",
      "Even your School Council is built on this idea: pupils ask why, and grown-ups listen and answer. This is called <strong>democracy</strong> — and it only works when people speak up, kindly and clearly.",
    ],
    contextList: [],
    contextEnd: "When you put your hand up in class to ask a respectful question, you are doing something very important. You are practising being a citizen.",
    activityTitle: "The Three Whys",
    activity: [
      "Each pupil writes 3 'kind why?' questions in their workbook — about something they have noticed at school, on TV, or in the wider world that doesn't seem fair.",
      "In pairs, share your three whys. Pick the most interesting one between you.",
      "Each pair shares with the class.",
      "Together, choose ONE question to send (with the teacher's help) to the School Council.",
      "Track what happens — does anyone listen? Does anything change?",
    ],
    reflection: [
      "What is one rule in our school you would gently like to ask 'why?' about?",
      "What is the difference between asking why to learn — and asking why to argue?",
      "Why do you think Periyar lived so long and stayed so happy, even though his life's work was hard?",
      "If you could ask the Prime Minister one kind 'why?', what would it be?",
    ],
  },

  {
    n: 7,
    title: "A Constitution for Equals",
    unit: "Unit 4 · Justice & Citizenship",
    duration: "65 minutes",
    image: "babasaheb",
    objective: [
      { strong: "Explain", rest: " what a constitution is and why it matters." },
      { strong: "Describe", rest: " how Babasaheb Ambedkar shaped India's Constitution." },
      { strong: "Compare", rest: " India's Constitution with how Britain protects rights." },
    ],
    curriculum: "Rule of law, rights and responsibilities. History links: Magna Carta and Britain's long story of protecting freedom. Moral leadership.",
    storyTitle: "The Rulebook of a Nation",
    story: [
      "When India became free from British rule in 1947, the country was full of joy — but also full of questions. <em>How will we live together? What rules will protect every person? Will any group be left behind?</em>",
      "A small committee was chosen to write a 'rulebook' for the new country. They looked around the room and chose the wisest, most determined person they could think of: <strong>Dr. B.R. Ambedkar</strong> — the same little boy who had once been refused water at school.",
      "For nearly three years, Babasaheb worked late into the night, often unwell, pouring his learning and his pain into every page. The Constitution he helped write became the longest in the world. But its first words were simple. They said that <strong>every</strong> citizen of India is equal. That nobody — nobody — is more important than anyone else.",
      "When he finished, an interviewer asked Babasaheb how he felt. He answered: <em>\"I have done what I could. The rest is up to the people.\"</em>",
    ],
    storyDiscussion: "Why was it important that Babasaheb — who had himself been treated unfairly — was the one chosen to write the rulebook? What did he understand that others might have missed?",
    conceptTitle: "What is a Constitution?",
    concept: [
      "A <strong>constitution</strong> is the most important rulebook of a country. It says:",
      "Who is in charge. What the leaders are allowed to do. What rights every person has. What happens if the rules are broken.",
      "A constitution is like the lines on a football pitch. Without lines, no one knows where the game is played. With clear lines, the game is fair — and everyone can play.",
    ],
    vocab: [
      ["constitution", "the most important rulebook of a country."],
      ["citizen", "a person who belongs to a country and shares its rights."],
      ["right", "something every person is allowed to expect by law."],
      ["responsibility", "something each of us is meant to do for others."],
    ],
    contextTitle: "How Britain Protects Rights",
    context: [
      "Britain is unusual: it does <strong>not</strong> have one single written constitution like India or America. Instead, British rights are written across many famous documents and laws:",
    ],
    contextList: [
      "<strong>Magna Carta (1215)</strong> &mdash; the first time even the king was made to follow the law.",
      "<strong>The Bill of Rights (1689)</strong> &mdash; limits on royal power, freedom of speech in Parliament.",
      "<strong>Modern laws on equality</strong> &mdash; protection from unfair treatment, whoever you are.",
      "<strong>Rules that protect everyone</strong> &mdash; life, liberty, fair trial, no torture, freedom of belief.",
    ],
    contextEnd: "So when you walk into a British school, a British library, a British hospital — you are walking into a place protected by hundreds of years of careful rulemaking. India's path was different and faster. But the goal of both is the same: <em>every person, equal and protected</em>.",
    activityTitle: "Our Class Constitution",
    activity: [
      "As a whole class, brainstorm: what makes our class fair?",
      "Together, draft 5 short rules — phrased as rights, not just rules. (\"Every pupil has the right to be heard.\" \"Every pupil has the right to feel safe.\")",
      "Vote on the rules. (Every vote counts equally.)",
      "Write the final list on a big sheet. Everyone signs it.",
      "Display it on the classroom wall as the \"Class Constitution\".",
    ],
    reflection: [
      "Which class right do you think will be hardest to live up to? Why?",
      "If Babasaheb visited our classroom, what do you think he would say?",
      "Can you think of a right we have at school that children 100 years ago did not have?",
      "What is one responsibility you have because of the rights you enjoy?",
    ],
  },

  {
    n: 8,
    title: "The Eightfold Path",
    unit: "Unit 2 · The Path of Dhamma",
    duration: "60 minutes",
    image: "wheel",
    objective: [
      { strong: "List", rest: " the eight 'limbs' of the Buddha's path in their own words." },
      { strong: "Recognise", rest: " that the path is about how we live, not just what we know." },
      { strong: "Choose", rest: " one limb to practise this week." },
    ],
    curriculum: "Core Buddhist teachings. Making good choices. The values we live by together as a community.",
    storyTitle: "The First Lesson",
    story: [
      "When the Buddha became enlightened, his first thought was: <em>'This is wonderful. But it is so subtle, so deep — would anyone listen?'</em>",
      "He decided to try. He walked many days to a place called Sarnath, near the great river Ganges, where five of his old friends were waiting. They had once thought he had given up. But when they saw his calm, glowing face, they knew something had changed.",
      "Sitting in a deer park, the Buddha gave his very first teaching. He drew a wheel in the dust — a wheel with eight spokes — and called it the <strong>Noble Eightfold Path</strong>. \"This,\" he said, \"is how to live a life with less suffering and more kindness.\"",
      "The wheel he drew is the same wheel you see on the flag of India today, and at the heart of the ABGN-UK emblem.",
    ],
    storyDiscussion: "Why a wheel — and not a list, or a ladder? What does a wheel suggest about how the eight things go together?",
    conceptTitle: "Eight Ways to Live Skilfully",
    concept: [
      "The eight 'spokes' of the path are eight gentle promises we can make. They are about <strong>how we see, speak, act, work and think</strong>:",
    ],
    vocab: [
      ["wholesome", "good for you and good for others."],
      ["intention", "what you mean to do — your inner aim."],
      ["effort", "the gentle energy you put into something."],
      ["concentration", "the ability to keep your mind on one thing."],
    ],
    contextTitle: "The Eight Spokes (Made Simple)",
    context: ["For KS2 children, here are the eight spokes in plain English:"],
    contextList: [
      "<strong>Right Understanding</strong> — see things as they really are.",
      "<strong>Right Intention</strong> — mean well in everything you do.",
      "<strong>Right Speech</strong> — speak truthfully, kindly, helpfully.",
      "<strong>Right Action</strong> — act in ways that don't hurt others.",
      "<strong>Right Livelihood</strong> — when you grow up, choose work that helps the world.",
      "<strong>Right Effort</strong> — try gently, try again, don't give up.",
      "<strong>Right Mindfulness</strong> — pay attention to the present moment.",
      "<strong>Right Concentration</strong> — train the mind to be calm and focused.",
    ],
    contextEnd: "These are not rules with punishments. They are <strong>habits of the heart</strong> — small daily choices that, over a lifetime, shape who we become.",
    activityTitle: "My Spoke of the Week",
    activity: [
      "Each pupil reads through the eight spokes.",
      "Choose ONE that feels useful for you this week. (For example: 'Right Speech — I will try not to say unkind things.')",
      "Write your spoke in your workbook with an example of what you'll do.",
      "On Friday, share with a partner: how did it go? Was it easy or hard?",
      "Draw your own dharma wheel with your spoke at the top.",
    ],
    reflection: [
      "Which spoke do you think is the easiest? Which is the hardest?",
      "Why do you think the Buddha taught a 'path' instead of a single rule?",
      "Can you remember a time when 'Right Speech' would have helped you?",
      "Stretch: which spoke do grown-ups in your home find hardest? (Be kind in your answer!)",
    ],
  },

  {
    n: 9,
    title: "The Monkey King's Bridge",
    unit: "Unit 5 · Stories of Wisdom",
    duration: "55 minutes",
    image: "wheel",
    objective: [
      { strong: "Retell", rest: " the Mahakapi Jataka tale." },
      { strong: "Identify", rest: " what makes a leader brave and kind." },
      { strong: "Connect", rest: " the story to leaders they admire today." },
    ],
    curriculum: "Jataka tales (stories of the Buddha's past lives). English: storytelling and retelling. Leadership, kindness and teamwork.",
    storyTitle: "A Bridge Made of Heart",
    story: [
      "Long, long ago, in a forest in India, there lived a giant Monkey King. He led 80,000 monkeys. They lived in a great mango tree by a river — eating sweet fruit, swinging and laughing all day.",
      "One year a human king came hunting in the forest. He saw the mango tree and fell in love with the fruit. \"<em>Bring me the monkeys</em>,\" he ordered his archers. \"<em>I want this whole tree for myself.</em>\"",
      "The Monkey King heard the soldiers coming. He told his troop to leap to safety on the far bank. But the gap was too wide. The young monkeys would never make it. So the Monkey King did something extraordinary. He stretched his enormous body across the river — making his own arms into a bridge — and held on while every single monkey ran across his back to safety.",
      "When the last monkey had crossed, the Monkey King's strength gave out. The human king, watching from below, was so moved that he climbed up to help. \"<em>Why did you do this?</em>\" he asked. The Monkey King smiled. \"<em>Because I am their king. A king is the bridge his people walk on.</em>\"",
      "From that day, the human king ruled with kindness, never forgetting what he had seen.",
    ],
    storyDiscussion: "What does it mean to be 'a bridge people walk on'? Have you ever seen someone do something quietly so others could be safe or happy?",
    conceptTitle: "Brave + Kind = Real Leadership",
    concept: [
      "The Buddha told this story (a <strong>Jataka tale</strong>) to show that real leadership isn't about being the loudest or the strongest. It is about <strong>caring more for others than for yourself</strong>.",
      "A bossy person says: \"<em>Do what I say.</em>\" A leader says: \"<em>Let me help.</em>\"",
      "The Monkey King had two qualities together: <strong>bravery</strong> (he was willing to stretch himself) and <strong>kindness</strong> (he stretched himself <em>for others</em>). Either one alone is not enough. Together, they make leaders we never forget.",
    ],
    vocab: [
      ["jataka", "a story about one of the Buddha's many past lives."],
      ["leader", "someone who guides others, often by example."],
      ["sacrifice", "giving something up so someone else can have more."],
      ["loyalty", "staying faithful to your people, especially when it's hard."],
    ],
    contextTitle: "Bridge-Builders in Britain Today",
    context: [
      "In every community in the UK there are bridge-builder leaders. Some are famous: a footballer like Marcus Rashford raising his voice for hungry children. A nurse working extra shifts during a hard winter. A teacher who stays late to help a struggling pupil.",
      "Most are not famous at all: the older sister who walks her brother to school every day; the grandparent who helps a neighbour with shopping; the school librarian who quietly orders the books their pupils ask for.",
    ],
    contextList: [],
    contextEnd: "These people are bridges. They use their own strength so others can cross.",
    activityTitle: "My Hero Bridge",
    activity: [
      "Think of one person who has been a 'bridge' in your life. (It can be famous OR someone in your family.)",
      "Draw a wide bridge across an A4 page.",
      "On the bridge, write that person's name and one sentence about what they did.",
      "Underneath the bridge, write your own name — because someday, you can be the bridge for someone else.",
      "Display all bridges as a class corridor of heroes.",
    ],
    reflection: [
      "Who has been a 'bridge' for you?",
      "What does it cost a leader to stretch themselves the way the Monkey King did?",
      "Do you have to be older to be a leader? Or can a child be one too?",
      "Stretch: name one person in our school you would call a bridge-builder. What do they do?",
    ],
  },

  {
    n: 10,
    title: "Many Faiths, One Britain",
    unit: "Unit 4 · Justice & Citizenship",
    duration: "60 minutes",
    image: "wheel",
    objective: [
      { strong: "Name", rest: " several religions practised in modern Britain." },
      { strong: "Describe", rest: " what 'multi-faith respect' means." },
      { strong: "Reflect", rest: " on the rich diversity in their own town or city." },
    ],
    curriculum: "Multi-faith awareness and respect for difference. Diversity in modern Britain. Living together with kindness.",
    storyTitle: "A Walk Through One Town",
    story: [
      "Imagine taking a slow Saturday-morning walk through almost any town in Britain — Birmingham, Leicester, Bradford, Glasgow or your own town.",
      "You might pass a <strong>church</strong>, with people in their best clothes singing. Around the corner, a <strong>mosque</strong>, where worshippers are removing their shoes and praying together. Down the street, a <strong>gurdwara</strong>, where the kitchen is open and anyone — anyone at all — can sit on the floor and share a free meal.",
      "Walking on, you might find a <strong>mandir</strong> with marigold garlands at the door, a <strong>synagogue</strong> with a stone star above the gate, and tucked away in a quiet street, a small <strong>vihara</strong> — a Buddhist centre — with shoes lined up outside and the soft scent of incense.",
      "All of these are British. All of these are home to British families. None is more British than any other. This is one of the gentle wonders of our country.",
    ],
    storyDiscussion: "How many different places of worship do you know in your own town? Have you been inside any?",
    conceptTitle: "Multi-Faith Respect",
    concept: [
      "<strong>Multi-faith respect</strong> means honouring the beliefs of others, even when they are very different from our own.",
      "It does NOT mean we all secretly believe the same thing. We don't. It means we accept that other people, made just like us, have come to different conclusions about big questions — and that this is okay.",
      "We listen carefully. We ask kind questions. We don't laugh at what we don't understand. We celebrate when our friends celebrate. And we expect the same in return.",
    ],
    vocab: [
      ["faith", "a deep belief about life, often about something greater than us."],
      ["worship", "showing reverence — through song, silence, prayer, or service."],
      ["diverse", "having many different kinds together."],
      ["tolerance", "respecting others' beliefs, even when different to your own."],
    ],
    contextTitle: "Britain's Shared Values · Mutual Respect",
    context: [
      "One of the values quietly taught in every British school is <em>mutual respect and tolerance for those of different faiths and beliefs</em>. This isn't just an idea &mdash; it's the lived reality of how we live together in Britain.",
      "Britain has been a multi-faith country for centuries. Today, our schools, hospitals, sports teams and Parliament include people of every faith and of no faith. We learn from one another — and we are stronger for it.",
    ],
    contextList: [],
    contextEnd: "When a school invites a Sikh elder, a Christian vicar, a Jewish rabbi, a Buddhist teacher and a humanist speaker to talk about kindness — and all five say the same thing — children learn the most important lesson of all: <em>under the surface, our hearts are very alike</em>.",
    activityTitle: "The Faith Map of Our Town",
    activity: [
      "Together, draw a simple map of your town or area on a big sheet.",
      "Mark places of worship you know. Use different symbols for each (cross, crescent, dharma wheel, star, khanda, om).",
      "Talk about which ones you have visited or would like to visit.",
      "Add famous local places where everyone is welcome: the library, the swimming pool, the park.",
      "Notice: the map of our town is also a map of our friendships.",
    ],
    reflection: [
      "Which place of worship would you most like to learn more about? Why?",
      "Have you ever been welcomed into a friend's celebration that was different from your own family's?",
      "What is one question you would respectfully ask a person of a different faith?",
      "Why do you think Britain has chosen 'mutual respect' as one of its four core values?",
    ],
  },

  {
    n: 11,
    title: "The Kind Emperor",
    unit: "Unit 5 · Stories of Wisdom",
    duration: "60 minutes",
    image: "ashoka",
    objective: [
      { strong: "Explain", rest: " how Emperor Ashoka changed after the war of Kalinga." },
      { strong: "Identify", rest: " three good things he did for his people." },
      { strong: "Imagine", rest: " how power can be used for good in everyday life." },
    ],
    curriculum: "Historical figures and Buddhist heritage. Ancient civilisations. The use of power for the common good.",
    storyTitle: "The Battlefield That Changed an Emperor",
    story: [
      "Over 2,300 years ago, there was a young emperor in India named <strong>Ashoka</strong>. He ruled almost the whole of the Indian subcontinent — a kingdom larger than today's Europe. Like many emperors, he wanted to make his empire even bigger.",
      "He invaded a kingdom called Kalinga. The battle was won. But when Ashoka walked across the battlefield afterwards, he saw something he had never let himself see before: thousands of dead and wounded, mothers crying for sons, children calling for fathers, animals lost and frightened.",
      "Something inside him broke. <em>'I won,'</em> he said quietly, <em>'but at what cost?'</em> He sat down on the muddy field and decided: <strong>no more</strong>. He would never wage a war for empire again.",
      "From that day, Ashoka followed the Buddha's path. He built hospitals — for people <em>and</em> for animals. He planted trees along every road so travellers could rest in shade. He dug wells, built rest-houses for pilgrims, and carved his promises onto huge stone pillars across his empire — the famous <strong>Ashoka Edicts</strong>, some of which can still be read today.",
      "He stopped being an emperor of armies. He became an emperor of <strong>kindness</strong>.",
    ],
    storyDiscussion: "What do you think made Ashoka able to change? Why did seeing the battlefield with his own eyes matter more than hearing about it?",
    conceptTitle: "Power Used for Good",
    concept: [
      "Ashoka had something most of us will never have: the power to command armies, build cities, change laws. But the most powerful thing he ever did was <strong>change his own heart</strong>.",
      "When power is used to hurt, it leaves wounds for generations. When power is used to help, it leaves shade trees, schools, hospitals — gifts that last hundreds of years.",
      "We may not be emperors. But we have power too — over the words we use, the friends we choose, the way we treat younger pupils, the kindness we show.",
    ],
    vocab: [
      ["emperor", "the ruler of a very large kingdom (an empire)."],
      ["edict", "an important order or message, often written down for everyone to read."],
      ["pillar", "a tall column of stone or wood — Ashoka used pillars to share his messages."],
      ["legacy", "the good things a person leaves behind after their lifetime."],
    ],
    contextTitle: "Service in Modern Britain",
    context: [
      "Britain has its own tradition of using power for good: the <strong>NHS</strong> (the National Health Service), free schooling for every child, free public libraries, the BBC, Citizens Advice, the RNLI lifeboats.",
      "These are modern 'shade trees and wells' — gifts the country has built so that people can be cared for, not just the rich. The famous Beveridge Report of 1942, which led to the welfare state, was a kind of British 'edict' — a promise that no one in Britain should be left without help when they truly need it.",
    ],
    contextList: [],
    contextEnd: "Ashoka would have approved.",
    activityTitle: "Our Class Edict",
    activity: [
      "On a long strip of paper (a 'pillar in miniature'), the class together composes an Edict of Kindness.",
      "Begin with: \"<em>We, the pupils of Class ___, declare these promises…</em>\"",
      "Add 5 simple promises (e.g., \"<em>We will help anyone who looks lonely at break time.</em>\")",
      "Decorate with a four-headed lion (Ashoka's symbol — also India's national emblem).",
      "Display in the school hall — a real edict, with real promises.",
    ],
    reflection: [
      "What is one bit of 'power' YOU have at school? How could you use it for good?",
      "Why is it harder to forgive after a battle than before one?",
      "Which of Ashoka's actions (hospitals, trees, wells, edicts) do you think mattered most? Why?",
      "Do you know any 'modern Ashokas' — people with power who have used it to help?",
    ],
  },

  {
    n: 12,
    title: "We Are the Next Story",
    unit: "Unit 6 · You and the Future",
    duration: "60 minutes",
    image: "babasaheb",
    objective: [
      { strong: "Reflect", rest: " on what they have learned across the whole book." },
      { strong: "Identify", rest: " one promise they want to make to their community." },
      { strong: "Recognise", rest: " that they are the next chapter of this long story." },
    ],
    curriculum: "Personal goals and identity. Active participation in our community. Meaning, purpose and what we leave behind for others.",
    storyTitle: "The Lamp That Was Passed On",
    story: [
      "Many years ago, the Buddha sat with his students one evening. The sun was setting. One of them looked sad. \"<em>Teacher</em>,\" he asked, \"<em>when you are gone, who will light our way?</em>\"",
      "The Buddha smiled. He picked up a small clay lamp — the kind everyone used in those days — and lit it from a single flame. Then he held the lamp out and said: \"<em>This flame can light a thousand other lamps. And when it does, the first lamp will not be any darker.</em>\"",
      "He paused. \"<em>You</em>\", he said, \"<em>will be the next lamp. And the next. And the next. The light has never depended on me. It has always depended on what each of you chooses to do with what you have learned.</em>\"",
      "And so it has been. From the Buddha to Samrat Ashoka. From Ashoka to Shivaji's just rule. From Shivaji to Mahatma Phule and Savitribai opening the first school for girls. From Phule to Periyar's brave 'why?'. From Periyar to Babasaheb writing the Constitution. From Babasaheb to Anna Bhau Sathe singing the songs of ordinary people. And from all of them — to <strong>you</strong>, sitting in this classroom, in Britain, in the year you are reading these words.",
      "<strong>You are the next lamp.</strong>",
    ],
    storyDiscussion: "Why does the Buddha use a lamp as the picture, instead of (say) a crown or a key?",
    conceptTitle: "We Carry the Light",
    concept: [
      "Through this whole book, we have met heroes from very different places and times. But every single one of them shared something simple: they <strong>cared more about others than about themselves</strong>.",
      "We will not all be emperors or constitution-writers. We probably won't open new schools or write 35 books. But we will each, every day, hold a lamp. The lamp of how we treat the new child in the playground. The lamp of how we speak to a tired parent. The lamp of how we use our little bit of power.",
      "The flame is in our hands now. What will we do with it?",
    ],
    vocab: [
      ["legacy", "the good gifts and lessons we leave for those who come after."],
      ["citizen", "a person who belongs to a country and helps care for it."],
      ["promise", "something we say we will do — and try our hardest to keep."],
      ["community", "all the people we share our lives with."],
    ],
    contextTitle: "Young Changemakers in Britain",
    context: [
      "Children and young people in modern Britain are already changing the world:",
    ],
    contextList: [
      "<strong>Marcus Rashford</strong> — campaigned for free school meals in school holidays.",
      "<strong>Bobbi Wegener and other young campaigners</strong> — raising money for refugee children in their local towns.",
      "<strong>Pupil-led eco councils</strong> — across thousands of UK schools, organising recycling, planting trees, cutting waste.",
      "<strong>Young carers</strong> — children who look after a parent or sibling, quietly and lovingly, every single day.",
    ],
    contextEnd: "None of them are emperors. None of them are world-famous (yet). But all of them have picked up the lamp.",
    activityTitle: "My Promise — Sealed Envelope",
    activity: [
      "Each pupil receives a small envelope.",
      "On a card inside, finish this sentence: \"<em>This year, my promise to my community is…</em>\"",
      "Sign and date your promise.",
      "Seal the envelope. Write your name on the front.",
      "The teacher keeps all envelopes safely.",
      "At the end of the school year, every pupil opens their own envelope and reads their promise. Did you keep it? Are you proud?",
    ],
    reflection: [
      "Which of the heroes we met in this book made the biggest impression on you? Why?",
      "If the Buddha handed YOU a small clay lamp, what would you light first?",
      "What is one tiny way your home or community is already a lamp for someone else?",
      "Stretch: in 30 years' time, when you are an adult, what would you like a child to read about <em>you</em>?",
    ],
  },
];
